import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { clients as defaultClients } from "../mock";

export default function Clients() {
  const [allClients, setAllClients] = useState(defaultClients);

  useEffect(() => {
    fetch("/api/content/clients")
      .then((r) => r.ok ? r.json() : null)
      .then((json) => { if (json?.data) setAllClients(json.data); })
      .catch(() => {});
  }, []);

  const half = Math.ceil(allClients.length / 2);
  const rowA = allClients.slice(0, half);
  const rowB = allClients.slice(half);

  return (
    <section id="clients" className="relative py-14 lg:py-20 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center max-w-2xl mx-auto mb-10"
        >
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-[#73100D]/25 bg-[#73100D]/5 text-[#73100D] text-[11px] font-semibold tracking-[0.18em] uppercase mb-5">
            Our Clientele
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-[#111] leading-tight">
            Trusted by businesses{" "}
            <span className="brand-gradient-text">across the globe</span>
          </h2>
          <p className="mt-4 text-[#555]">
            A snippet of the businesses across IT/ITES, Healthcare, Finance, Jewelry, Retail, Fashion and Consumer Goods we&rsquo;ve partnered with.
          </p>
        </motion.div>

        <div className="space-y-6">
          <ClientMarquee items={[...rowA, ...rowA]} duration="55s" />
          <ClientMarquee items={[...rowB, ...rowB]} duration="65s" reverse />
        </div>
      </div>

      {/* Edge fades */}
      <div className="pointer-events-none absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-white to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-white to-transparent" />
    </section>
  );
}

function ClientMarquee({ items, duration = "55s", reverse = false }) {
  return (
    <div className="relative overflow-hidden">
      <div
        className="flex gap-5 w-max"
        style={{
          animation: `marquee ${duration} linear infinite`,
          animationDirection: reverse ? "reverse" : "normal",
        }}
      >
        {items.map((c, i) => (
          <ClientCard key={`${c.name}-${i}`} client={c} />
        ))}
      </div>
    </div>
  );
}

function ClientCard({ client }) {
  const initials = client.name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

  const [imgErr, setImgErr] = React.useState(false);
  const hasLogo = client.logo && !imgErr;

  return (
    <div
      title={client.name}
      className="group shrink-0 relative flex items-center gap-3 px-5 h-16 rounded-2xl border border-[#E5E5E5] bg-white hover:border-[#73100D]/30 hover:shadow-[0_18px_40px_-18px_rgba(115,16,13,0.18)] hover:-translate-y-1 transition-all duration-300 min-w-[180px]"
    >
      <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-[#73100D] to-[#5E0000] grid place-items-center text-white font-mono font-bold text-sm shrink-0 overflow-hidden">
        {hasLogo ? (
          <img src={client.logo} alt={client.name} onError={() => setImgErr(true)}
            className="w-full h-full object-contain p-1.5 bg-white rounded-xl" />
        ) : (
          <>
            <span className="relative z-10">{initials}</span>
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.35),transparent_60%)]" />
          </>
        )}
      </div>
      <div className="text-sm font-semibold text-[#111] group-hover:text-[#73100D] transition-colors whitespace-nowrap">
        {client.name}
      </div>
    </div>
  );
}
